import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import Button from "../components/Button";
import DateRangeFilter from "../components/DateRangeFilter";
import FilterDropdown from "../components/FilterDropdown";
import Pagination from "../components/Pagination";
import SectionHeader from "../components/SectionHeader";
import StatCard, { StatGrid } from "../components/StatCard";
import Tag from "../components/Tag";
import TextLink from "../components/TextLink";
import controlStyles from "../components/ListControls.module.css";
import tableStyles from "../components/DataTable.module.css";
import downloadCsv from "../downloadCsv";
import { apiRequest } from "../api";
import { formatDate, formatNumber, formatRelative } from "../format";
import useApiQuery, { useDebounced } from "../useApiQuery";
import {
  SUPPORT_STATS,
  TICKET_AGES,
  TICKET_CATEGORIES,
  TICKET_PRIORITIES,
  TICKET_STATUS,
  ticketAge,
} from "./supportData";
import styles from "./SupportPage.module.css";

const PAGE_SIZE = 15;
const EXPORT_LIMIT = 5000;

// Widths include the cell gutters and add up to the table's min-width
const COLUMNS = [
  { label: "Status", width: 186 },
  { label: "Ticket ID", width: 120 },
  { label: "Trader", width: 120 },
  { label: "Category", width: 140 },
  { label: "Issue", width: 220 },
  { label: "Assigned to", width: 140 },
  { label: "Submitted", width: 120 },
  { label: "Last update", width: 126 },
];

const toOptions = (map) =>
  Object.entries(map).map(([value, entry]) => ({ value, label: typeof entry === "string" ? entry : entry.label }));

const EMPTY_FILTERS = {
  status: null,
  category: null,
  assignee: null,
  age: null,
  submitted: { from: "", to: "" },
};

const formatStat = (value, unit) => (value == null ? "—" : `${formatNumber(value)}${unit ? ` ${unit}` : ""}`);

// The filters as GET /api/admin/support/tickets reads them
const toQuery = (filters, q) => ({
  q,
  status: filters.status,
  category: filters.category,
  assignee: filters.assignee,
  age: filters.age,
  submittedFrom: filters.submitted.from,
  submittedTo: filters.submitted.to,
});

function exportCsv(tickets) {
  downloadCsv(
    "tickets.csv",
    ["Status", "Ticket ID", "Trader", "Email", "Category", "Issue", "Assigned to", "Submitted", "Last update", "Priority"],
    tickets.map((ticket) => [
      TICKET_STATUS[ticket.status].label,
      ticket.id,
      ticket.trader?.name ?? "",
      ticket.trader?.email ?? "",
      TICKET_CATEGORIES[ticket.category],
      ticket.subject,
      ticket.assignee?.name ?? "Unassigned",
      new Date(ticket.createdAt).toISOString(),
      new Date(ticket.lastActivityAt).toISOString(),
      TICKET_PRIORITIES[ticket.priority],
    ])
  );
}

export default function SupportPage({ onNavigate }) {
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  const debouncedQuery = useDebounced(query.trim());
  const apiQuery = toQuery(filters, debouncedQuery);
  const { data, status: loadStatus, reload } = useApiQuery("/api/admin/support/tickets", {
    ...apiQuery,
    page,
    pageSize: PAGE_SIZE,
  });

  // The team, for the "Assigned to" filter
  const team = useApiQuery("/api/admin/team");
  const filterDefs = useMemo(
    () => [
      { key: "status", label: "Status", options: toOptions(TICKET_STATUS) },
      { key: "category", label: "Category", options: toOptions(TICKET_CATEGORIES) },
      {
        key: "assignee",
        label: "Assigned to",
        options: [
          { value: "unassigned", label: "Unassigned" },
          ...(team.data?.members ?? [])
            .filter((member) => member.status === "active")
            .map((member) => ({ value: member.id, label: member.name })),
        ],
      },
      { key: "age", label: "Age", options: toOptions(TICKET_AGES) },
    ],
    [team.data]
  );

  const tickets = data?.tickets ?? [];
  const stats = data?.stats;
  const pageCount = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const filtered = query.trim() !== "" || Object.entries(filters).some(([key, value]) =>
    key === "submitted" ? value.from || value.to : value !== null
  );

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };

  const openTicket = (ticket) => onNavigate(`/support/${ticket.id}`);

  const exportTickets = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const result = await apiRequest("/api/admin/support/tickets", {
        query: { ...apiQuery, page: 1, pageSize: EXPORT_LIMIT },
      });
      exportCsv(result.tickets);
    } catch (err) {
      setExportError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const now = Date.now();

  return (
    <section className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.title}>Support</h1>
        <p className={styles.subtitle}>Review support requests, respond to traders and manage help content.</p>
      </header>

      <section>
        <SectionHeader title="Overview" />
        <StatGrid>
          {SUPPORT_STATS.map(({ key, label, unit }) => (
            <StatCard key={key} label={label} value={formatStat(stats?.[key], unit)} />
          ))}
        </StatGrid>
      </section>

      <section>
        <SectionHeader title="Ticket queue" />
        <div className={`${controlStyles.toolbar} ${styles.toolbar} d-flex align-items-center justify-content-between`}>
          <label className={`${controlStyles.search} d-flex align-items-center`}>
            <Search size={20} strokeWidth={1.75} aria-hidden="true" />
            <input
              type="search"
              placeholder="Search ticket"
              aria-label="Search ticket"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
            />
          </label>
          <Button variant="outline" disabled={exporting} onClick={exportTickets}>
            {exporting ? "Exporting…" : "Export tickets"}
          </Button>
        </div>
        {exportError ? <p className={controlStyles.empty}>{exportError}</p> : null}

        <div className={`${controlStyles.filters} d-flex flex-wrap`}>
          {filterDefs.map((filter) => (
            <FilterDropdown
              key={filter.key}
              label={filter.label}
              options={filter.options}
              value={filters[filter.key]}
              onChange={(value) => updateFilter(filter.key, value)}
            />
          ))}
          <DateRangeFilter
            label="Date"
            value={filters.submitted}
            onChange={(value) => updateFilter("submitted", value)}
          />
        </div>

        <div className={tableStyles.wrap}>
          <table className={`${tableStyles.table} ${tableStyles.fixed} ${styles.table}`}>
            <colgroup>
              {COLUMNS.map((column) => (
                <col key={column.label} style={{ width: column.width }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {COLUMNS.map((column) => (
                  <th key={column.label} scope="col">
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tickets.map((ticket) => {
                const status = TICKET_STATUS[ticket.status];

                return (
                  <tr
                    key={ticket.id}
                    className={tableStyles.clickable}
                    onClick={() => openTicket(ticket)}
                    onKeyDown={(event) => event.key === "Enter" && openTicket(ticket)}
                    tabIndex={0}
                  >
                    <td>
                      <Tag tone={status.tone}>{status.label}</Tag>
                    </td>
                    <td>{ticket.id}</td>
                    <td title={ticket.trader?.email}>{ticket.trader?.name ?? "—"}</td>
                    <td>{TICKET_CATEGORIES[ticket.category]}</td>
                    <td className={styles.issue} title={ticket.subject}>
                      {ticket.subject}
                    </td>
                    <td>{ticket.assignee?.name ?? "Unassigned"}</td>
                    <td title={formatDate(ticket.createdAt)}>{TICKET_AGES[ticketAge(ticket.createdAt, now)]}</td>
                    <td>{formatRelative(ticket.lastActivityAt, now)}</td>
                  </tr>
                );
              })}
              {!data && loadStatus === "loading" ? (
                <tr>
                  <td colSpan={COLUMNS.length} className={controlStyles.empty}>
                    Loading tickets…
                  </td>
                </tr>
              ) : loadStatus === "error" ? (
                <tr>
                  <td colSpan={COLUMNS.length} className={controlStyles.empty}>
                    We couldn't load tickets. <TextLink arrow={false} onClick={reload}>Try again</TextLink>
                  </td>
                </tr>
              ) : data && tickets.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} className={controlStyles.empty}>
                    {filtered ? "No tickets match these filters." : "No support tickets yet."}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <Pagination page={currentPage} pageCount={pageCount} onChange={setPage} />
      </section>
    </section>
  );
}
